/**
 * @module PlatformTypes
 * @purpose TypeScript types mirroring the platform status and demo-mode API response shapes.
 * @depends backend platform_status / demo_mode endpoints (camelCase serde output)
 * @dependedOnBy services/platform.ts, hooks consuming platform status, demo-mode components
 *
 * @rules
 * - Performance: types only — no runtime logic beyond the error-code constant object
 * - State: n/a
 * - Errors: DemoModeErrorCode mirrors the backend error-code registry for the demo-mode endpoints
 */

export type DemoCapability = 'externalLlmSwitch' | 'caseReset' | 'duplicatePackageOverride'

export type PlatformEnvironment = 'dev' | 'demo' | 'production'

export type DemoLlmProvider = 'primary' | 'alternate'

/**
 * Demo mode status. `enabledBy`, `enabledAt`, and `llmProvider` are only
 * present on the authenticated response shape.
 */
export interface DemoModeStatus {
  enabled: boolean
  enabledBy?: string
  enabledAt?: string
  llmProvider?: DemoLlmProvider
}

/**
 * `GET /api/platform/status` response. `environment` and `capabilities` are
 * only present on the authenticated response shape.
 */
export interface PlatformStatus {
  demoMode: DemoModeStatus
  environment?: PlatformEnvironment
  capabilities?: DemoCapability[]
}

export interface ToggleDemoModeRequest {
  enabled: boolean
  pin: string
  currentPassword: string
}

export interface SetDemoLlmProviderRequest {
  provider: DemoLlmProvider
}

export interface PlatformEvent {
  id: string
  message: string
  actor: string
  action: string
  occurredAt: string
}

export interface ListPlatformEventsResponse {
  events: PlatformEvent[]
}

/**
 * Mirror of the backend demo-mode error-code registry (PRP §4). Keep aligned
 * with the Rust error variants for `PUT /platform/demo-mode` and
 * `PUT /platform/demo-mode/llm-provider`.
 */
export const DemoModeErrorCodes = {
  DEMO_MODE_UNSUPPORTED_ENVIRONMENT: 'DEMO_MODE_UNSUPPORTED_ENVIRONMENT',
  DEMO_MODE_DENIED: 'DEMO_MODE_DENIED',
  DEMO_MODE_PIN_NOT_CONFIGURED: 'DEMO_MODE_PIN_NOT_CONFIGURED',
  DEMO_MODE_RATE_LIMITED: 'DEMO_MODE_RATE_LIMITED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  DEMO_MODE_REQUIRED: 'DEMO_MODE_REQUIRED',
  ALTERNATE_PROFILE_NOT_CONFIGURED: 'ALTERNATE_PROFILE_NOT_CONFIGURED',
  FORBIDDEN: 'FORBIDDEN',
} as const

export type DemoModeErrorCode = (typeof DemoModeErrorCodes)[keyof typeof DemoModeErrorCodes]
